import { useCallback } from 'react';

import {
	FastshiftRunbookProduct,
	FastshiftRunbookStepStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { useFastshiftAnalyticsData, useFireAnalyticsEvents } from '@/analytics';
import { ActionKey, ActionSubjectKey, ActionSubjectIdKey } from '@/analytics/constants';

import { RunbookStepActionType } from '../types';

type RunbooksPanelDetails = {
	supportTicketId: string;
	runbookCount: number;
};

type RunbookDetails = {
	supportTicketId: string;
	runbookId: string;
	product: FastshiftRunbookProduct;
	stepCount: number;
};

type RunbookStepContext = {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	stepOrder: number;
	phase: string;
};

type RunbookStepActionsMenuEvent = RunbookStepContext & {
	disabled: boolean;
	risk: boolean;
	status: FastshiftRunbookStepStatus;
};

type RunbookStepStatusToggleEvent = RunbookStepContext & {
	targetStatus: FastshiftRunbookStepStatus;
	isNoteRequired: boolean;
};

type RunbookStepRiskToggleEvent = RunbookStepContext & {
	targetRisk: boolean;
};

type RunbookStepDisableToggleEvent = RunbookStepContext & {
	targetDisabled: boolean;
};

type RunbookStepActionErrorFlagEvent = {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	actionType: RunbookStepActionType;
};

type RunbookStepStatusConfirmModalEvent = {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	targetStatus: FastshiftRunbookStepStatus;
	isNoteRequired: boolean;
};

type RunbookStepDisableConfirmEvent = {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
	targetDisabled: boolean;
};

type RunbookStepDisableCancelEvent = {
	supportTicketId: string;
	runbookId: string;
	stepId: string;
};

export const useRunbooksAnalytics = () => {
	const { sourceKey: uiSourceKey, identityAttrs: uiIdentityAttrs } =
		useFastshiftAnalyticsData(true);
	const { sourceKey: trackSourceKey, identityAttrs: trackIdentityAttrs } =
		useFastshiftAnalyticsData();
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);
	const { fireTrackEvent } = useFireAnalyticsEvents(trackIdentityAttrs);

	const sendRunbooksPanelDetailsTrackEvent = useCallback(
		({ supportTicketId, runbookCount }: RunbooksPanelDetails) => {
			if (!trackIdentityAttrs) {
				return false;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.SECTION,
				actionSubjectId: ActionSubjectIdKey.RUNBOOKS_PANEL_DETAILS,
				source: trackSourceKey,
				attributes: {
					supportTicketId,
					runbookCount,
				},
			});
			return true;
		},
		[fireTrackEvent, trackIdentityAttrs, trackSourceKey],
	);

	const sendRunbooksPanelDismissButtonClickUIEvent = useCallback(
		(supportTicketId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOKS_PANEL_DISMISS_BUTTON,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookCardExpandToggleUIEvent = useCallback(
		(supportTicketId: string, runbookId: string, isExpanded: boolean) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_CARD_EXPAND_TOGGLE,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					isExpanded,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookDetailsTrackEvent = useCallback(
		({ supportTicketId, runbookId, product, stepCount }: RunbookDetails) => {
			if (!trackIdentityAttrs) {
				return false;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.SECTION,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_DETAILS,
				source: trackSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					product,
					stepCount,
				},
			});
			return true;
		},
		[fireTrackEvent, trackIdentityAttrs, trackSourceKey],
	);

	const sendRunbookOpenInMarathonLinkClickUIEvent = useCallback(
		(supportTicketId: string, runbookId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_OPEN_IN_MARATHON_LINK,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepActionsMenuUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			stepOrder,
			phase,
			disabled,
			risk,
			status,
		}: RunbookStepActionsMenuEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_ACTIONS_MENU,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					stepOrder,
					phase,
					disabled,
					risk,
					status,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepStatusToggleUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			stepOrder,
			phase,
			targetStatus,
			isNoteRequired,
		}: RunbookStepStatusToggleEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_STATUS_TOGGLE,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					stepOrder,
					phase,
					targetStatus,
					isNoteRequired,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepRiskToggleUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			stepOrder,
			phase,
			targetRisk,
		}: RunbookStepRiskToggleEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_RISK_TOGGLE,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					stepOrder,
					phase,
					targetRisk,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepDisableToggleUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			stepOrder,
			phase,
			targetDisabled,
		}: RunbookStepDisableToggleEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_DISABLE_TOGGLE,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					stepOrder,
					phase,
					targetDisabled,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepActionErrorFlagTrackEvent = useCallback(
		({ supportTicketId, runbookId, stepId, actionType }: RunbookStepActionErrorFlagEvent) => {
			if (!trackIdentityAttrs) {
				return;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.FLAG,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_ACTION_ERROR_FLAG,
				source: trackSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					actionType,
				},
			});
		},
		[fireTrackEvent, trackIdentityAttrs, trackSourceKey],
	);

	const sendRunbookStepStatusConfirmModalTrackEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			targetStatus,
			isNoteRequired,
		}: RunbookStepStatusConfirmModalEvent) => {
			if (!trackIdentityAttrs) {
				return;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.MODAL,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_STATUS_CONFIRM_MODAL,
				source: trackSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					targetStatus,
					isNoteRequired,
				},
			});
		},
		[fireTrackEvent, trackIdentityAttrs, trackSourceKey],
	);

	const sendRunbookStepStatusConfirmButtonClickUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			targetStatus,
			isNoteRequired,
		}: RunbookStepStatusConfirmModalEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_STATUS_CONFIRM_BUTTON,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					targetStatus,
					isNoteRequired,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepStatusConfirmCancelButtonClickUIEvent = useCallback(
		({
			supportTicketId,
			runbookId,
			stepId,
			targetStatus,
			isNoteRequired,
		}: RunbookStepStatusConfirmModalEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_STATUS_CONFIRM_CANCEL_BUTTON,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					targetStatus,
					isNoteRequired,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepDisableConfirmModalTrackEvent = useCallback(
		({ supportTicketId, runbookId, stepId, targetDisabled }: RunbookStepDisableConfirmEvent) => {
			if (!trackIdentityAttrs) {
				return;
			}

			fireTrackEvent({
				action: ActionKey.DISPLAYED,
				actionSubject: ActionSubjectKey.MODAL,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_DISABLE_CONFIRM_MODAL,
				source: trackSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					targetDisabled,
				},
			});
		},
		[fireTrackEvent, trackIdentityAttrs, trackSourceKey],
	);

	const sendRunbookStepDisableConfirmButtonClickUIEvent = useCallback(
		({ supportTicketId, runbookId, stepId, targetDisabled }: RunbookStepDisableConfirmEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_DISABLE_CONFIRM_BUTTON,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
					targetDisabled,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	const sendRunbookStepDisableCancelButtonClickUIEvent = useCallback(
		({ supportTicketId, runbookId, stepId }: RunbookStepDisableCancelEvent) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RUNBOOK_STEP_DISABLE_CANCEL_BUTTON,
				source: uiSourceKey,
				attributes: {
					supportTicketId,
					runbookId,
					stepId,
				},
			});
		},
		[fireUIEvent, uiSourceKey],
	);

	return {
		sendRunbooksPanelDetailsTrackEvent,
		sendRunbooksPanelDismissButtonClickUIEvent,
		sendRunbookCardExpandToggleUIEvent,
		sendRunbookDetailsTrackEvent,
		sendRunbookOpenInMarathonLinkClickUIEvent,
		sendRunbookStepActionsMenuUIEvent,
		sendRunbookStepStatusToggleUIEvent,
		sendRunbookStepRiskToggleUIEvent,
		sendRunbookStepDisableToggleUIEvent,
		sendRunbookStepActionErrorFlagTrackEvent,
		sendRunbookStepStatusConfirmModalTrackEvent,
		sendRunbookStepStatusConfirmButtonClickUIEvent,
		sendRunbookStepStatusConfirmCancelButtonClickUIEvent,
		sendRunbookStepDisableConfirmModalTrackEvent,
		sendRunbookStepDisableConfirmButtonClickUIEvent,
		sendRunbookStepDisableCancelButtonClickUIEvent,
	};
};
